import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

// Submissions go to Netlify Forms. The schema is registered from the hidden
// stub form in index.html, because this is a client-rendered SPA and Netlify
// only parses static HTML at deploy time. Field name attributes below must
// match that stub exactly.
const FORM_NAME = 'contact';

export default function Contact() {
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    const body = new URLSearchParams(new FormData(e.currentTarget));
    body.set('form-name', FORM_NAME);
    setStatus('sending');
    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus('sent');
    } catch {
      // Only ever confirm what was actually accepted.
      setStatus('error');
    }
  };

  return (
    <div className="pt-32 pb-24 bg-slate-900 text-white min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-20">
          <div>
            <h1 className="text-5xl font-serif mb-6">Let's start a conversation.</h1>
            <p className="text-xl text-slate-400 font-light mb-12">
              Reach out to our global team to discuss how we can partner to drive transformation in your organization.
            </p>
            
            <div className="space-y-8">
              <div className="flex gap-6">
                <div className="w-12 h-12 bg-amber-600 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiIcons.FiMail} className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Email Us</h3>
                  <p className="text-slate-400">inquiries@eldenwyck.com</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="w-12 h-12 bg-amber-600 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiIcons.FiMapPin} className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Global Headquarters</h3>
                  <p className="text-slate-400">123 Strategy Way, London, EC1A 1BB, United Kingdom</p>
                </div>
              </div>
            </div>
          </div>

          {status === 'sent' ? (
            <div role="status" className="bg-white p-10 shadow-2xl">
              <h2 className="text-2xl font-serif text-slate-900 mb-3">Thank you — received.</h2>
              <p className="text-slate-600">A member of our team will be in touch shortly.</p>
            </div>
          ) : (
          <form
            name={FORM_NAME}
            method="POST"
            action="/"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
            className="bg-white p-10 shadow-2xl space-y-6"
          >
            <h2 className="text-2xl font-serif text-slate-900 mb-4">Inquiry Form</h2>
            <input type="hidden" name="form-name" value={FORM_NAME} />
            <p className="hidden" aria-hidden="true">
              <label>Do not fill this in<input name="bot-field" tabIndex={-1} autoComplete="off" /></label>
            </p>
            {status === 'error' && (
              <p role="alert" className="text-sm font-bold text-red-700">
                That did not send. Please try again, or email{' '}
                <a href="mailto:hello@eldenwyck.co" className="underline">hello@eldenwyck.co</a>.
              </p>
            )}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="ew-first-name" className="text-xs font-bold uppercase tracking-widest text-slate-500">First Name</label>
                <input id="ew-first-name" name="first-name" type="text" required autoComplete="given-name" className="w-full border-b-2 border-slate-200 py-2 text-slate-900 focus:border-amber-600 outline-none transition-colors" />
              </div>
              <div className="space-y-2">
                <label htmlFor="ew-last-name" className="text-xs font-bold uppercase tracking-widest text-slate-500">Last Name</label>
                <input id="ew-last-name" name="last-name" type="text" required autoComplete="family-name" className="w-full border-b-2 border-slate-200 py-2 text-slate-900 focus:border-amber-600 outline-none transition-colors" />
              </div>
            </div>
            <div className="space-y-2">
              <label htmlFor="ew-work-email" className="text-xs font-bold uppercase tracking-widest text-slate-500">Work Email</label>
              <input id="ew-work-email" name="work-email" type="email" required autoComplete="email" className="w-full border-b-2 border-slate-200 py-2 text-slate-900 focus:border-amber-600 outline-none transition-colors" />
            </div>
            <div className="space-y-2">
              <label htmlFor="ew-message" className="text-xs font-bold uppercase tracking-widest text-slate-500">Message</label>
              <textarea id="ew-message" name="message" rows="4" required className="w-full border-b-2 border-slate-200 py-2 text-slate-900 focus:border-amber-600 outline-none transition-colors resize-none"></textarea>
            </div>
            <button type="submit" disabled={status === 'sending'} className="w-full bg-slate-900 text-white py-4 font-bold hover:bg-slate-800 transition-colors uppercase tracking-widest text-sm disabled:opacity-60">
              {status === 'sending' ? 'Sending…' : 'Send Message'}
            </button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}